import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  createPlanStage,
  deletePlan,
  deletePlanStage,
  getPlan,
  getPlanRoute,
  reorderPlanStages,
  updatePlan,
  updatePlanStage,
  type PlanDetail,
  type PlanRoute,
  type PlanStage,
} from '../api/plans';
import { HttpError } from '../api/client';
import { reverseGeocode, type Geo } from '../api/geocoding';
import { PlanMap } from '../components/PlanMap';
import { AddressSearch } from '../components/AddressSearch';
import { AddressBookPicker } from '../components/AddressBookPicker';
import { PoiPicker } from '../components/PoiPicker';
import { SortablePlanStages } from '../components/SortablePlanStages';
import { ConvertPlanModal } from '../components/ConvertPlanModal';
import { LodgingFields, type LodgingFormState } from '../components/LodgingFields';
import type { LodgingType, StageLodgingDto } from '@camper-diary/shared';
import { parseCoordinate } from '../utils/coordinates';

function emptyLodgingForm(): LodgingFormState {
  return { name: '', type: '', phone: '', url: '', lat: '', lon: '', notes: '', useForRouting: false };
}

function lodgingFromStage(l: StageLodgingDto): LodgingFormState {
  return {
    name: l.name ?? '',
    type: l.type ?? '',
    phone: l.phone ?? '',
    url: l.url ?? '',
    lat: l.lat !== null ? l.lat.toString() : '',
    lon: l.lon !== null ? l.lon.toString() : '',
    notes: l.notes ?? '',
    useForRouting: l.useForRouting,
  };
}

function lodgingPayload(f: LodgingFormState): Partial<StageLodgingDto> | null {
  const name = f.name.trim();
  const phone = f.phone.trim();
  const url = f.url.trim();
  const notes = f.notes.trim();
  const type = f.type ? (f.type as LodgingType) : null;
  const latNum = f.lat.trim() ? parseCoordinate(f.lat) : null;
  const lonNum = f.lon.trim() ? parseCoordinate(f.lon) : null;
  const validCoords =
    latNum !== null && lonNum !== null &&
    latNum >= -90 && latNum <= 90 && lonNum >= -180 && lonNum <= 180;
  if (!name && !type && !phone && !url && !validCoords && !notes) return null;
  return {
    name: name || null,
    type: type ?? null,
    phone: phone || null,
    url: url || null,
    lat: validCoords ? latNum : null,
    lon: validCoords ? lonNum : null,
    notes: notes || null,
    // il flag ha senso solo con coordinate alloggio valide
    useForRouting: validCoords ? f.useForRouting : false,
  };
}

export function PlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [plan, setPlan] = useState<PlanDetail | null>(null);
  const [route, setRoute] = useState<PlanRoute>(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Quick-add bar: sempre visibile. Quando editingStageId è valorizzato si
  // sta modificando una tappa esistente; altrimenti si crea una nuova tappa.
  const [editingStageId, setEditingStageId] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formLat, setFormLat] = useState('');
  const [formLon, setFormLon] = useState('');
  const [formAddress, setFormAddress] = useState('');
  const [formInfo, setFormInfo] = useState('');
  const [formLodging, setFormLodging] = useState<LodgingFormState>(emptyLodgingForm());
  const [formPlannedDate, setFormPlannedDate] = useState('');
  // Posizione 1-based dove inserire la nuova tappa. 'end' = append in fondo
  // (default). Valori numerici "1".."N" inseriscono al rank corrispondente
  // facendo scalare le successive. Ignorato in fase di edit.
  const [formPosition, setFormPosition] = useState<string>('end');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Per scrollare/evidenziare la barra quando l'utente clicca "Modifica" su una tappa.
  const quickAddRef = useRef<HTMLFormElement | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editingStageId) {
      // L'editor inline si apre sotto la tappa, quindi non serve scrollare
      // tutta la pagina; mettiamo solo il focus sul titolo.
      setTimeout(() => titleInputRef.current?.focus(), 80);
    }
  }, [editingStageId]);

  async function refresh() {
    if (!id) return;
    try {
      setPlan(await getPlan(id));
      setError(null);
    } catch (e) {
      if (e instanceof HttpError && e.status === 404) {
        setError('Pianificazione non trovata');
      } else {
        setError(e instanceof Error ? e.message : 'Errore caricamento');
      }
    }
  }

  async function refreshRoute() {
    if (!id) return;
    setRouteLoading(true);
    try {
      const r = await getPlanRoute(id);
      setRoute(r);
    } catch {
      // OSRM down: lascia route=null, niente alert
      setRoute(null);
    } finally {
      setRouteLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    void refreshRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Quando il numero o l'ordine delle tappe cambia, ricalcola la route.
  // (Non ricalcoliamo se cambia solo la data o le note di una tappa.)
  // Include il punto di routing dell'alloggio: cambiare il flag o le coordinate
  // alloggio deve far ricalcolare i km, non solo lo spostamento delle tappe.
  const stageRouteKey =
    plan?.stages
      .map(
        (s) =>
          `${s.id}:${s.lat}:${s.lon}:${s.lodging.useForRouting ? 1 : 0}:${s.lodging.lat ?? ''}:${s.lodging.lon ?? ''}`,
      )
      .join('|') ?? '';
  useEffect(() => {
    if (!id || !plan) return;
    if (plan.stages.length < 2) {
      setRoute(null);
      return;
    }
    void refreshRoute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageRouteKey]);

  if (error) {
    return (
      <div className="page">
        <p className="err">{error}</p>
        <Link to="/plans" className="btn btn-ghost">
          ← Torna alle pianificazioni
        </Link>
      </div>
    );
  }
  if (!plan) {
    return (
      <div className="page">
        <p className="muted">Caricamento…</p>
      </div>
    );
  }

  function clearForm() {
    setEditingStageId(null);
    setFormTitle('');
    setFormLat('');
    setFormLon('');
    setFormAddress('');
    setFormInfo('');
    setFormLodging(emptyLodgingForm());
    setFormPlannedDate('');
    setFormPosition('end');
    setDetailsOpen(false);
    setFormError(null);
  }

  function startEdit(s: PlanStage) {
    setEditingStageId(s.id);
    setFormTitle(s.title);
    setFormLat(s.lat.toString());
    setFormLon(s.lon.toString());
    setFormAddress(s.address ?? '');
    setFormInfo(s.info ?? '');
    setFormLodging(lodgingFromStage(s.lodging));
    setFormPlannedDate(s.plannedDate ?? '');
    const hasLodging =
      s.lodging.name || s.lodging.type || s.lodging.phone || s.lodging.url ||
      (s.lodging.lat !== null && s.lodging.lon !== null) || s.lodging.notes;
    // Apri il pannello dettagli se la tappa ha già info/alloggio/data,
    // così l'utente vede subito cosa sta modificando.
    setDetailsOpen(Boolean(s.info || hasLodging || s.plannedDate));
    setFormError(null);
  }

  function onAddressPick(r: Geo) {
    if (!formTitle.trim()) {
      // primo segmento del display name come titolo di default
      setFormTitle(r.displayName.split(',')[0]?.trim() ?? '');
    }
    setFormLat(r.lat.toFixed(6));
    setFormLon(r.lon.toFixed(6));
    setFormAddress(r.displayName);
  }

  function onAddressBookPick(entry: {
    label: string;
    lat: number;
    lon: number;
    address: string | null;
  }) {
    if (!formTitle.trim()) setFormTitle(entry.label);
    setFormLat(entry.lat.toFixed(6));
    setFormLon(entry.lon.toFixed(6));
    setFormAddress(entry.address ?? '');
  }

  function onPoiPick(p: { name: string; lat: number; lon: number; address: string | null }) {
    if (!formTitle.trim()) setFormTitle(p.name);
    setFormLat(p.lat.toFixed(6));
    setFormLon(p.lon.toFixed(6));
    setFormAddress(p.address ?? '');
  }

  const pickPos = (() => {
    const lat = parseCoordinate(formLat);
    const lon = parseCoordinate(formLon);
    if (lat === null || lon === null) return null;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
    return { lat, lon };
  })();

  const onMapPick = (lat: number, lon: number) => {
    setFormLat(lat.toFixed(6));
    setFormLon(lon.toFixed(6));
    void (async () => {
      try {
        const r = await reverseGeocode(lat, lon);
        if (r) {
          setFormAddress((cur) => (cur.trim() === '' ? r.displayName : cur));
          if (!formTitle.trim()) {
            setFormTitle(r.displayName.split(',')[0]?.trim() ?? '');
          }
        }
      } catch {
        /* ignore */
      }
    })();
  };

  async function saveStage(e: FormEvent) {
    e.preventDefault();
    if (!plan) return;
    setFormError(null);
    const title = formTitle.trim();
    const lat = parseCoordinate(formLat);
    const lon = parseCoordinate(formLon);
    if (!title) {
      setFormError('Inserisci un titolo per la tappa');
      return;
    }
    if (lat === null || lat < -90 || lat > 90) {
      setFormError('Latitudine non valida — usa la mappa, la rubrica o cerca un indirizzo');
      return;
    }
    if (lon === null || lon < -180 || lon > 180) {
      setFormError('Longitudine non valida');
      return;
    }
    setSubmitting(true);
    try {
      const data = {
        title,
        lat,
        lon,
        address: formAddress.trim() || null,
        info: formInfo.trim() || null,
        plannedDate: formPlannedDate || null,
        lodging: lodgingPayload(formLodging),
      };
      if (editingStageId) {
        await updatePlanStage(plan.id, editingStageId, data);
      } else {
        // position 'end' = omettiamo il campo (il backend appende in fondo).
        const positionNum = formPosition === 'end' ? undefined : Number(formPosition);
        await createPlanStage(plan.id, {
          ...data,
          ...(positionNum && { position: positionNum }),
        });
      }
      clearForm();
      await refresh();
    } catch (err) {
      setFormError(err instanceof HttpError ? err.apiError.message : 'Errore di connessione');
    } finally {
      setSubmitting(false);
    }
  }

  async function onDeletePlan() {
    if (!plan) return;
    if (!confirm(`Eliminare la pianificazione "${plan.title}"?`)) return;
    try {
      await deletePlan(plan.id);
      navigate('/plans', { replace: true });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore');
    }
  }

  async function onDeleteStage(s: PlanStage) {
    if (!confirm(`Eliminare la tappa "${s.title}"?`)) return;
    setBusy(true);
    try {
      await deletePlanStage(plan!.id, s.id);
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore');
    } finally {
      setBusy(false);
    }
  }

  async function onReorder(newOrder: PlanStage[]) {
    if (!plan) return;
    // optimistic UI: aggiorna subito il client
    setPlan({ ...plan, stages: newOrder.map((s, i) => ({ ...s, orderIndex: i })) });
    setBusy(true);
    try {
      await reorderPlanStages(plan.id, newOrder.map((s) => s.id));
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore riordino');
      await refresh(); // ripristina dallo stato server
    } finally {
      setBusy(false);
    }
  }

  async function onExportPdf() {
    if (!plan) return;
    setExportingPdf(true);
    try {
      const res = await fetch(`/api/v1/plans/${plan.id}/pdf`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'fetch' },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error?.message ?? `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') ?? '';
      const m = /filename\*?=(?:UTF-8'')?["']?([^;"']+)/.exec(cd);
      a.download = m?.[1] ? decodeURIComponent(m[1]) : `${plan.title}-itinerario.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore export PDF');
    } finally {
      setExportingPdf(false);
    }
  }

  async function onStageDateChange(s: PlanStage, newDate: string | null) {
    if (!plan) return;
    setPlan({
      ...plan,
      stages: plan.stages.map((x) => (x.id === s.id ? { ...x, plannedDate: newDate } : x)),
    });
    try {
      await updatePlanStage(plan.id, s.id, { plannedDate: newDate });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore aggiornamento data');
      await refresh();
    }
  }

  // Form unico: viene renderizzato in cima quando si crea una nuova tappa,
  // oppure inline (sotto la tappa cliccata) quando si modifica una tappa
  // esistente. La stessa istanza JSX si sposta — gli stati restano qui sopra
  // (formTitle, formLat, …) quindi non si perde nulla nella transizione.
  const stageFormJsx = (
    <form className="quick-add-bar" onSubmit={saveStage} ref={quickAddRef}>
      {editingStageId && (
        <div className="qa-banner">
          ✏️ Stai modificando questa tappa.
          <button
            type="button"
            className="link-btn"
            onClick={clearForm}
            disabled={submitting}
          >
            Annulla modifica
          </button>
        </div>
      )}
      <label className="qa-field qa-title-field">
        <span>Titolo *</span>
        <input
          ref={titleInputRef}
          className="qa-title"
          type="text"
          value={formTitle}
          onChange={(e) => setFormTitle(e.target.value)}
          placeholder="Titolo (es. Casa, Bobbio…)"
          maxLength={200}
          required
        />
      </label>
      <div className="qa-row qa-row-tools">
        <AddressBookPicker onPick={onAddressBookPick} />
        <PoiPicker onPick={onPoiPick} />
        <div className="qa-search">
          <AddressSearch
            value={formAddress}
            onChange={setFormAddress}
            onPick={onAddressPick}
            placeholder="Cerca indirizzo, luogo o Plus Code…"
          />
        </div>
      </div>
      <div className="qa-row qa-row-main">
        <div className="qa-coords">
          {pickPos ? (
            <span>
              📍 <strong>{pickPos.lat.toFixed(4)}, {pickPos.lon.toFixed(4)}</strong>
            </span>
          ) : (
            <span className="muted">Coordinate: cerca, rubrica o click sulla mappa</span>
          )}
        </div>
        {/* Selettore posizione: solo in modalita nuova tappa, e solo quando
            ha senso scegliere (>=2 tappe). Per "in fondo" valore 'end' che il
            saveStage traduce in omissione del campo position. */}
        {!editingStageId && plan.stages.length >= 2 && (
          <label className="qa-position" title="Dove inserire la tappa">
            <span>Posizione</span>
            <select
              value={formPosition}
              onChange={(e) => setFormPosition(e.target.value)}
            >
              <option value="end">In fondo ({plan.stages.length + 1}ª)</option>
              <option value="1">All'inizio (1ª)</option>
              {plan.stages.slice(0, -1).map((s, i) => (
                <option key={s.id} value={String(i + 2)}>
                  Dopo {i + 1}. {s.title} ({i + 2}ª)
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="qa-actions">
          <button
            type="button"
            className="btn btn-ghost qa-toggle"
            onClick={() => setDetailsOpen((v) => !v)}
            aria-expanded={detailsOpen}
            title="Aggiungi descrizione, note o data prevista"
          >
            {detailsOpen ? '▾ Dettagli' : '▸ Dettagli'}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !formTitle.trim() || !pickPos}
            title={editingStageId ? 'Salva modifiche' : 'Aggiungi tappa'}
          >
            {editingStageId ? '💾 Salva' : '+ Aggiungi'}
          </button>
          {editingStageId && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={clearForm}
              disabled={submitting}
            >
              Annulla
            </button>
          )}
        </div>
      </div>
      {detailsOpen && (
        <div className="qa-details">
          <label className="qa-field">
            <span>Data prevista</span>
            <input
              type="date"
              value={formPlannedDate}
              onChange={(e) => setFormPlannedDate(e.target.value)}
            />
          </label>
          <label className="qa-field">
            <span>Info utili</span>
            <textarea
              rows={4}
              value={formInfo}
              onChange={(e) => setFormInfo(e.target.value)}
              maxLength={5000}
              placeholder="Descrizione, servizi, recensione, note pratiche…"
            />
          </label>
          <LodgingFields
            value={formLodging}
            onChange={setFormLodging}
            stageLat={formLat}
            stageLon={formLon}
          />
        </div>
      )}
      {formError && <p className="err">{formError}</p>}
    </form>
  );

  return (
    <div className="page">
      <div className="page-head">
        <div className="title-block">
          {editingTitle ? (
            <PlanTitleEditor
              plan={plan}
              onSaved={async () => {
                setEditingTitle(false);
                await refresh();
              }}
              onCancel={() => setEditingTitle(false)}
            />
          ) : (
            <>
              <h1 className="cd-title-upper">
                {plan.title}{' '}
                <button className="link-btn" onClick={() => setEditingTitle(true)} title="Rinomina">
                  ✏️
                </button>
              </h1>
              {plan.description && <p className="muted">{plan.description}</p>}
            </>
          )}
        </div>
        <div className="action-row">
          <button
            className="btn btn-ghost"
            onClick={() => void onExportPdf()}
            disabled={exportingPdf || plan.stages.length === 0}
            title={
              plan.stages.length === 0
                ? 'Aggiungi almeno una tappa per esportare l’itinerario'
                : 'Esporta itinerario in PDF (mappa, tappe, km, tempi, note)'
            }
          >
            {exportingPdf ? '📖 Generazione…' : '📖 Esporta PDF'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setConvertOpen(true)}
            disabled={plan.stages.length === 0}
            title={
              plan.stages.length === 0
                ? 'Aggiungi almeno una tappa per creare il viaggio'
                : 'Crea un nuovo viaggio dalla pianificazione'
            }
          >
            🚐 Crea viaggio dal piano
          </button>
          <button className="btn btn-danger" onClick={() => void onDeletePlan()}>
            Elimina
          </button>
        </div>
      </div>

      {/* Layout split: mappa sempre visibile a destra (sticky su desktop,
          sticky-top con altezza ridotta su mobile). Vedi .plan-split in CSS. */}
      <div className="plan-split">
        <div className="plan-split-left">
          <div className="section-head">
            <h2>Tappe ({plan.stages.length})</h2>
          </div>

          {!editingStageId && stageFormJsx}

          {plan.stages.length === 0 && (
            <p className="muted">
              Nessuna tappa. Compila il modulo qui sopra (titolo + cerca/rubrica/click sulla mappa)
              e premi <strong>+ Aggiungi</strong>.
            </p>
          )}

          {plan.stages.length > 0 && (
            <>
              <SortablePlanStages
                stages={plan.stages}
                legs={route?.legs ?? null}
                legsLoading={routeLoading}
                busy={busy}
                editingStageId={editingStageId}
                inlineEditor={editingStageId ? stageFormJsx : null}
                onReorder={onReorder}
                onEdit={startEdit}
                onDelete={onDeleteStage}
                onDateChange={onStageDateChange}
              />
              {route && plan.stages.length >= 2 && (
                <p className="route-total muted">
                  Totale stradale: <strong>~{Math.round(route.distanceKm)} km</strong> ·{' '}
                  {formatHours(route.durationHours)} di guida
                </p>
              )}
            </>
          )}
        </div>

        <div className="plan-split-right">
          <PlanMap
            points={plan.stages.map((s) => ({
              id: s.id,
              title: s.title,
              address: s.address,
              lat: s.lat,
              lon: s.lon,
            }))}
            lodgingPoints={plan.stages
              .filter(
                (s) =>
                  s.lodging.useForRouting &&
                  s.lodging.lat !== null &&
                  s.lodging.lon !== null,
              )
              .map((s) => ({
                id: s.id,
                title: s.lodging.name ?? s.title,
                lat: s.lodging.lat!,
                lon: s.lodging.lon!,
              }))}
            pick={pickPos}
            onPick={onMapPick}
            routePolyline={route?.polyline ?? null}
            height="100%"
          />
        </div>
      </div>

      {convertOpen && (
        <ConvertPlanModal
          plan={plan}
          onClose={() => setConvertOpen(false)}
          onConverted={(tripId) => navigate(`/trips/${tripId}`)}
        />
      )}

      <Link to="/plans" className="btn btn-ghost back">
        ← Torna alle pianificazioni
      </Link>
    </div>
  );
}

function PlanTitleEditor({
  plan,
  onSaved,
  onCancel,
}: {
  plan: PlanDetail;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(plan.title);
  const [description, setDescription] = useState(plan.description ?? '');
  const [saving, setSaving] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await updatePlan(plan.id, {
        title: title.trim(),
        description: description.trim() || null,
      });
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Errore');
      setSaving(false);
    }
  }

  return (
    <form className="form-card inline-title-edit" onSubmit={save}>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        required
        autoFocus
        maxLength={200}
      />
      <textarea
        rows={2}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={5000}
        placeholder="Descrizione (opzionale)"
      />
      <div className="actions">
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
          Annulla
        </button>
        <button type="submit" disabled={saving || !title.trim()}>
          {saving ? '…' : 'Salva'}
        </button>
      </div>
    </form>
  );
}

function formatHours(h: number): string {
  if (h < 1) return `${Math.round(h * 60)} min`;
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return mm > 0 ? `${hh}h ${mm}m` : `${hh}h`;
}
